/*
 * a11y-enhance.js — runtime accessibility enhancer.
 *
 * SCOPE / CONSTRAINT: This file ONLY ever changes the page while the user has
 * accessibility mode enabled (root element has the `a11y-enabled` class, set by
 * snippets/accessibility-preferences.liquid + assets/a11y-prefs-init.js). When
 * accessibility mode is OFF, this script makes ZERO changes — and when the user
 * toggles it off live, every change made here is reverted, so the default
 * ("normal") experience is left exactly as the theme ships it.
 *
 * It is purely ADDITIVE: it injects missing ARIA semantics, accessible names,
 * and keyboard handlers that the static templates lack. It never alters layout,
 * colors, or visible content. CSS-based effects (contrast, spacing, focus,
 * reduced motion) live in accessibility-preferences.liquid; this file covers the
 * things that can only be fixed in markup/behavior.
 *
 * Covered (audit P2/P3/P4):
 *  - P2  Carousel prev/next arrows + pagination bullets get names & keyboard ops
 *  - P3  Modal/popup containers get role="dialog" / aria-modal / aria-label
 *        (focus-trap, Escape, and focus-return are already handled by
 *         theme.Modals — we do NOT duplicate them)
 *  - P4  Form fixes: aria-required, broken career file-upload label, filter
 *        checkbox groups, price-range input labels
 */
(function () {
  'use strict';

  var ROOT = document.documentElement;
  var ENABLED_CLASS = 'a11y-enabled';

  // Each enhancer records how to undo itself so we can cleanly revert when the
  // user turns accessibility mode off (keeping normal mode untouched).
  var reverts = [];
  var processed = new WeakSet(); // nodes we've already enhanced (idempotency)
  var liveRegion = null;         // shared polite live region for status messages
  var cartLastCount = null;      // last known cart item_count (for add/remove wording)
  var searchLastCount = -1;      // last announced search suggestion count

  function isEnabled() {
    return ROOT.classList.contains(ENABLED_CLASS);
  }

  // Add an attribute only if absent; register a revert that removes it again.
  function addAttr(el, name, value) {
    if (el.hasAttribute(name)) return;
    el.setAttribute(name, value);
    reverts.push(function () {
      // Only remove if it still holds the value we set (don't clobber later state)
      if (el.getAttribute(name) === value) el.removeAttribute(name);
    });
  }

  function addListener(el, type, handler, opts) {
    el.addEventListener(type, handler, opts);
    reverts.push(function () {
      el.removeEventListener(type, handler, opts);
    });
  }

  // Set an attribute to a new value (overwriting if present) and register a
  // revert that restores the previous value (or removes it if there was none).
  function setAttrTracked(el, name, value) {
    var prev = el.getAttribute(name);
    if (prev === value) return;
    el.setAttribute(name, value);
    reverts.push(function () {
      if (el.getAttribute(name) === value) {
        if (prev === null) el.removeAttribute(name);
        else el.setAttribute(name, prev);
      }
    });
  }

  // Activate Enter/Space on a non-native control (mirrors a click).
  function keyActivate(e) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      e.currentTarget.click();
    }
  }

  function firstHeadingText(container) {
    var h = container.querySelector('h1, h2, h3, h4, [role="heading"]');
    if (h) {
      var t = (h.textContent || '').replace(/\s+/g, ' ').trim();
      if (t) return t;
    }
    return '';
  }

  function eachNew(selector, fn) {
    var nodes = document.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (processed.has(el)) continue;
      processed.add(el);
      try { fn(el); } catch (err) { /* never let one node break the sweep */ }
    }
  }

  /* ============================================================
     P2 — Carousels: accessible names + keyboard operability
     Swiper prev/next are frequently empty <div>s with no name and
     no keyboard support. Only touch ones missing a name so already
     labelled instances (e.g. new-collab-section) are left alone.
     WCAG 4.1.2 (Name, Role, Value), 2.1.1 (Keyboard).
     ============================================================ */
  function enhanceCarousels() {
    var pairs = [
      ['.swiper-button-prev:not([aria-label])', 'Previous slide'],
      ['.swiper-button-next:not([aria-label])', 'Next slide']
    ];
    pairs.forEach(function (pair) {
      eachNew(pair[0], function (el) {
        addAttr(el, 'aria-label', pair[1]);
        // If it isn't a native button, make it focusable + operable.
        if (el.tagName !== 'BUTTON' && el.tagName !== 'A') {
          addAttr(el, 'role', 'button');
          if (!el.hasAttribute('tabindex')) addAttr(el, 'tabindex', '0');
          addListener(el, 'keydown', keyActivate);
        }
      });
    });

    // Pagination bullets that are exposed as buttons (role+tabindex) but have no
    // key handler attached — make Enter/Space work.
    eachNew('.swiper-pagination-bullet[role="button"][tabindex]', function (el) {
      addListener(el, 'keydown', keyActivate);
    });
  }

  /* ============================================================
     P3 — Dialog semantics for modal/popup containers.
     theme.Modals already provides focus-trap, Escape-to-close, and
     focus-return; the only gap is the container missing dialog role,
     aria-modal, and an accessible name. We target real modals (those
     containing a .js-modal-close) plus the age-verification element.
     WCAG 4.1.2.
     ============================================================ */
  function enhanceDialogs() {
    var candidates = [];
    document.querySelectorAll('.modal').forEach(function (el) {
      if (el.querySelector('.js-modal-close')) candidates.push(el);
    });
    document.querySelectorAll('age-verification-popup').forEach(function (el) {
      candidates.push(el);
    });

    candidates.forEach(function (el) {
      if (processed.has(el)) return;
      processed.add(el);
      try {
        addAttr(el, 'role', 'dialog');
        addAttr(el, 'aria-modal', 'true');
        if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
          var name = firstHeadingText(el);
          if (!name) {
            if (el.tagName.toLowerCase() === 'age-verification-popup') name = 'Age verification';
            else if (el.id && /newsletter/i.test(el.id)) name = 'Newsletter sign-up';
            else name = 'Dialog';
          }
          addAttr(el, 'aria-label', name);
        }
      } catch (err) {}
    });
  }

  /* ============================================================
     P4a — aria-required on fields that are required but only say so
     via the HTML `required` attribute (some AT/UX benefit). 3.3.2.
     ============================================================ */
  function enhanceRequiredFields() {
    eachNew('input[required]:not([aria-required]), select[required]:not([aria-required]), textarea[required]:not([aria-required])', function (el) {
      addAttr(el, 'aria-required', 'true');
    });
  }

  /* ============================================================
     P4b — Career form: file-upload <label for> points at the wrong
     id ("ContactFormPosition" instead of "FileUpload"), leaving the
     file input unnamed. Re-point the label that sits in the same
     wrapper as the file input. 1.3.1 / 4.1.2.
     ============================================================ */
  function enhanceCareerFileLabel() {
    var fileInput = document.getElementById('FileUpload');
    if (!fileInput || processed.has(fileInput)) return;
    processed.add(fileInput);
    if (document.querySelector('label[for="FileUpload"]')) return; // already correct
    var wrapper = fileInput.closest('.mb-4') || fileInput.parentElement;
    var label = wrapper && wrapper.querySelector('label');
    if (label) {
      var prev = label.getAttribute('for');
      label.setAttribute('for', 'FileUpload');
      reverts.push(function () {
        if (label.getAttribute('for') === 'FileUpload') {
          if (prev === null) label.removeAttribute('for');
          else label.setAttribute('for', prev);
        }
      });
    }
  }

  /* ============================================================
     P4c — Collection filter checkbox groups: associate each group of
     filter checkboxes with its filter heading so AT announces the
     group name (e.g. "Size"). 1.3.1.
     ============================================================ */
  function enhanceFilterGroups() {
    eachNew('.tag-list:not(.tag-list--active-tags)', function (ul) {
      if (!ul.querySelector('input[type="checkbox"]')) return;
      if (ul.hasAttribute('aria-label') || ul.hasAttribute('role')) return;
      var group = ul.closest('.collection-sidebar__group');
      var header = group && group.querySelector('.tag-list__header');
      var name = header ? (header.textContent || '').replace(/\s+/g, ' ').trim() : '';
      if (!name) return;
      addAttr(ul, 'role', 'group');
      addAttr(ul, 'aria-label', name);
    });
  }

  /* ============================================================
     P4d — Price-range filter inputs have no accessible name. 1.3.1 / 3.3.2.
     ============================================================ */
  function enhancePriceRange() {
    eachNew('.price-range__input-min:not([aria-label])', function (el) {
      addAttr(el, 'aria-label', 'Minimum price');
    });
    eachNew('.price-range__input-max:not([aria-label])', function (el) {
      addAttr(el, 'aria-label', 'Maximum price');
    });
  }

  /* ============================================================
     P5a (axe) — Tab/category switcher buttons whose only text is in a
     child <p aria-hidden="true">, leaving the button with no name.
     Give the button an aria-label from that text. 4.1.2.
     e.g. sections/collection-card-slider.liquid
     ============================================================ */
  function enhanceTabButtons() {
    eachNew('button[data-btn]:not([aria-label]):not([aria-labelledby])', function (el) {
      var p = el.querySelector('p');
      var name = p ? (p.textContent || '').replace(/\s+/g, ' ').trim() : '';
      if (!name) name = (el.getAttribute('data-btn') || '').replace(/_/g, ' ').trim();
      if (name) addAttr(el, 'aria-label', name);
    });
  }

  /* ============================================================
     P5b (axe) — <iframe> elements with no accessible name. Title the
     known third-party promo frame plus any visible, non-hidden iframe
     that lacks a name. Tracking/hidden iframes (offscreen or
     aria-hidden) are left alone. 4.1.2 / 2.4.1.
     ============================================================ */
  function enhanceIframes() {
    var pbar = document.getElementById('PBarNextFrame');
    if (pbar && !processed.has(pbar)) {
      processed.add(pbar);
      if (!pbar.hasAttribute('title') && !pbar.hasAttribute('aria-label')) {
        addAttr(pbar, 'title', 'Promotional banner');
      }
    }
    eachNew('iframe:not([title]):not([aria-label]):not([aria-labelledby])', function (el) {
      if (el.getAttribute('aria-hidden') === 'true') return;
      if (el.offsetParent === null) return; // hidden / tracking pixel — don't name it
      addAttr(el, 'title', 'Embedded content');
    });
  }

  /* ============================================================
     P5c (axe) — Horizontally scrollable containers that aren't keyboard
     reachable. Make them a focusable, named region so keyboard users can
     scroll. Only when actually overflowing (re-checked each sweep until
     content/images settle). 2.1.1.
     ============================================================ */
  function enhanceScrollables() {
    var nodes = document.querySelectorAll('.carousel');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (processed.has(el)) continue;
      if (el.scrollWidth <= el.clientWidth + 2) continue; // not scrollable yet
      processed.add(el);
      try {
        addAttr(el, 'tabindex', '0');
        addAttr(el, 'role', 'region');
        if (!el.hasAttribute('aria-label')) {
          var section = el.closest('.as-seen-on--section') || el.parentElement;
          var h = section && section.querySelector('h1, h2, h3, h4, h5, h6');
          var name = h ? (h.textContent || '').replace(/\s+/g, ' ').trim() : 'Carousel';
          addAttr(el, 'aria-label', name + ' (scrollable region)');
        }
      } catch (err) {}
    }
  }

  /* ============================================================
     P5d (axe) — Heading-order skips (e.g. an <h4> following an <h2>).
     Re-level the offending heading via aria-level so the outline has no
     gap. Conservative: only the flagged "As seen on" section heading,
     and only when an actual skip exists. 1.3.1.
     ============================================================ */
  function headingLevel(h) {
    var al = h.getAttribute('aria-level');
    if (al && !isNaN(+al)) return +al;
    var m = /^H([1-6])$/.exec(h.tagName);
    return m ? +m[1] : 2;
  }
  function previousHeadingLevel(el) {
    var arr = [].slice.call(document.querySelectorAll('h1, h2, h3, h4, h5, h6, [role="heading"]'));
    var idx = arr.indexOf(el);
    for (var i = idx - 1; i >= 0; i--) {
      if (arr[i].offsetParent !== null) return headingLevel(arr[i]);
    }
    return null;
  }
  function enhanceHeadingOrder() {
    eachNew('.as-seen-on--section h4', function (el) {
      var prev = previousHeadingLevel(el);
      if (prev == null) return;
      if (4 > prev + 1) {
        addAttr(el, 'role', 'heading');
        addAttr(el, 'aria-level', String(prev + 1));
      }
    });
  }

  /* ============================================================
     P5e (axe) — PDP image zoom button is icon-only with no name. 4.1.2.
     ============================================================ */
  function enhanceIconButtons() {
    eachNew('button.js-photoswipe__zoom:not([aria-label]):not([aria-labelledby])', function (el) {
      addAttr(el, 'aria-label', 'Zoom image');
    });
  }

  /* ============================================================
     P5f (axe) — Links with an EMPTY aria-label (e.g. image-only hero slide
     links) have no accessible name. Derive one from the href. 2.4.4 / 4.1.2.
     Note: addAttr() won't overwrite an existing (empty) attribute, so this
     sets/reverts manually.
     ============================================================ */
  function humanizeSlug(s) {
    return (s || '').replace(/[-_]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }).trim();
  }
  function linkNameFromHref(href) {
    if (!href) return '';
    var path = href.split('?')[0].split('#')[0].replace(/\/+$/, '');
    var seg = path.split('/').filter(Boolean).pop() || '';
    var name = humanizeSlug(seg);
    if (!name) return '';
    return /\/collections\//.test(path) ? name + ' collection' : name;
  }
  function enhanceEmptyLinks() {
    eachNew('a.hero__slide-link', function (el) {
      var current = el.getAttribute('aria-label');
      if (current && current.trim()) return; // already named
      var name = linkNameFromHref(el.getAttribute('href')) || (el.textContent || '').trim() || 'Shop collection';
      el.setAttribute('aria-label', name);
      reverts.push(function () {
        if (el.getAttribute('aria-label') === name) {
          if (current === null) el.removeAttribute('aria-label');
          else el.setAttribute('aria-label', current);
        }
      });
    });
  }

  /* ============================================================
     P5g (auditor) — Desktop mega-menu: drilling into a level-2 item with
     children reveals the level-3 panel but the theme HIDES the level-2
     buttons (incl. the one you activated), so keyboard focus drops to
     <body> and the sub-menu becomes unreachable. After the panel opens,
     move focus into it. (Escape already closes the menu via the nav's own
     handler.) WCAG 2.1.1.
     ============================================================ */
  function enhanceMegaMenuKeyboard() {
    var nav = document.getElementById('DesktopNav');
    if (!nav || processed.has(nav)) return;
    processed.add(nav);
    var onActivate = function (e) {
      var btn = e.target.closest && e.target.closest('.has-children[data-link-level="2"]');
      if (!btn || !nav.contains(btn)) return;
      var panel = btn.nextElementSibling;
      if (!panel || !panel.classList || !panel.classList.contains('dynamic-links-level-3')) return;
      // Defer until after the theme's own click handler has revealed the panel
      // and hidden the level-2 buttons, then place focus inside it.
      var run = function () {
        if (panel.classList.contains('hide')) return; // didn't actually open
        var first = panel.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
        if (first) first.focus();
      };
      if (window.requestAnimationFrame) window.requestAnimationFrame(run);
      else window.setTimeout(run, 16);
    };
    nav.addEventListener('click', onActivate);
    reverts.push(function () { nav.removeEventListener('click', onActivate); });
  }

  /* ============================================================
     P5h (request) — Reveal the "has sub-menu" chevron while the TOP-LEVEL
     nav item (first tab stop) is focused, so keyboard users know a sub-menu
     exists before tabbing to the chevron toggle. The theme already keeps the
     chevron offscreen until the toggle itself (second tab) is focused, and
     rotates it down->up via [aria-expanded]. We add the first-tab reveal here
     (inline styles, applied directly) rather than via the inline <style> so it
     delivers reliably. Focus moving INTO the open menu keeps it revealed; once
     focus leaves the item entirely it returns to its default hidden state.
     ============================================================ */
  var CHEVRON_REVEAL = {
    position: 'static', width: 'auto', height: 'auto', opacity: '1',
    pointerEvents: 'auto', marginLeft: '-26px', marginRight: '22px',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
  };
  function setChevron(toggle, on) {
    for (var k in CHEVRON_REVEAL) {
      toggle.style[k] = on ? CHEVRON_REVEAL[k] : '';
    }
  }
  function enhanceNavChevronAffordance() {
    var nav = document.getElementById('DesktopNav');
    if (!nav) return;
    nav.querySelectorAll('.site-nav__details.first-level').forEach(function (d) {
      if (processed.has(d)) return;
      var toggle = d.nextElementSibling;
      if (!toggle || !toggle.classList || !toggle.classList.contains('nav-dropdown-toggle')) return;
      processed.add(d);
      var reveal = function () { setChevron(toggle, true); };
      var maybeHide = function () {
        // Defer so document.activeElement reflects the element being moved TO.
        window.setTimeout(function () {
          if (d.contains(document.activeElement) || document.activeElement === toggle) return;
          setChevron(toggle, false);
        }, 0);
      };
      d.addEventListener('focusin', reveal);
      d.addEventListener('focusout', maybeHide);
      reverts.push(function () {
        d.removeEventListener('focusin', reveal);
        d.removeEventListener('focusout', maybeHide);
        setChevron(toggle, false);
      });
    });
  }

  /* ============================================================
     P5i (request) — Classic desktop nav (.desktop-nav / header-desktop-nav)
     has NO chevron: parent items open their mega-menu only via the Down-arrow
     key or hover, so keyboard users get no "has sub-menu" affordance.

     Inject a chevron toggle as the SECOND tab stop right after each parent
     link. The chevron is a focusable tab stop but is VISUALLY HIDDEN until it
     receives focus (Apple-style) — so it only appears on the second Tab, never
     for mouse users or at rest. Flow:
       - 1st Tab  -> the link (Enter follows its URL, unchanged)
       - 2nd Tab  -> the chevron becomes visible, pointing DOWN
       - Enter/Space on the chevron -> opens the sub-menu; chevron flips UP
         (focus stays on the chevron so the up state is visible)
       - Down arrow on the chevron  -> opens and moves focus into the sub-menu
       - Escape -> closes, focus back on the chevron
     Visibility + rotation are handled by a runtime-injected stylesheet using
     real :focus / [aria-expanded] rules (reliable, no scroll jank). Injected
     only in accessibility mode and fully removed on toggle-off.
     WCAG 2.1.1 / 2.4.x.
     ============================================================ */
  var CHEVRON_STYLE_ID = 'a11y-nav-chevron-style';
  function ensureClassicNavChevronStyles() {
    if (document.getElementById(CHEVRON_STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = CHEVRON_STYLE_ID;
    style.textContent =
      '.a11y-nav-chevron{position:absolute;left:-9999px;width:1px;height:1px;opacity:0;' +
      'padding:0;margin:0;border:0;background:none;color:inherit;pointer-events:none;overflow:hidden;}' +
      '.a11y-nav-chevron:focus,.a11y-nav-chevron:focus-visible{position:static;left:auto;width:auto;' +
      'height:auto;opacity:1;padding:6px;margin:0 2px;pointer-events:auto;overflow:visible;' +
      'display:inline-flex;align-items:center;justify-content:center;vertical-align:middle;cursor:pointer;' +
      'outline:2px solid #0066CC;outline-offset:2px;border-radius:4px;}' +
      '.a11y-nav-chevron svg{display:block;transition:transform .2s ease;}' +
      '.a11y-nav-chevron[aria-expanded="true"] svg{transform:rotate(180deg);}';
    (document.head || document.documentElement).appendChild(style);
    reverts.push(function () { if (style.parentNode) style.parentNode.removeChild(style); });
  }
  function buildChevronButton(labelText, megaId) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'a11y-nav-chevron';
    btn.setAttribute('aria-expanded', 'false');
    if (megaId) btn.setAttribute('aria-controls', megaId);
    btn.setAttribute('aria-label', 'Toggle ' + labelText + ' sub-menu');
    btn.innerHTML = '<svg width="12" height="8" viewBox="0 0 10 6" fill="none" aria-hidden="true" ' +
      'focusable="false"><path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return btn;
  }
  function enhanceClassicNavChevrons() {
    var nav = document.querySelector('.desktop-nav');
    if (!nav) return;
    ensureClassicNavChevronStyles();
    nav.querySelectorAll('.desktop-nav__item').forEach(function (item) {
      if (processed.has(item)) return;
      var trigger = item.querySelector('.desktop-nav__link');
      var submenu = item.querySelector('.mega-menu');
      if (!trigger || !submenu) return; // item without a sub-menu: no chevron
      processed.add(item);

      var label = (trigger.textContent || 'menu').replace(/\s+/g, ' ').trim();
      var chevron = buildChevronButton(label, submenu.id);

      function setOpen(open) {
        item.classList.toggle('is-open', open);
        submenu.setAttribute('aria-hidden', open ? 'false' : 'true');
        if (trigger.hasAttribute('aria-expanded')) {
          trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
        chevron.setAttribute('aria-expanded', open ? 'true' : 'false'); // CSS rotates the svg
      }

      // Enter/Space (native button click): toggle, keeping focus on the chevron
      // so its up state is visible.
      var onClick = function (e) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(!item.classList.contains('is-open'));
      };
      var onKeydown = function (e) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (!item.classList.contains('is-open')) setOpen(true);
          var first = submenu.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
          if (first) window.setTimeout(function () { first.focus(); }, 0);
        } else if (e.key === 'Escape' && item.classList.contains('is-open')) {
          e.preventDefault();
          setOpen(false);
          chevron.focus();
        }
      };
      var onItemFocusOut = function () {
        window.setTimeout(function () {
          if (!item.contains(document.activeElement) && item.classList.contains('is-open')) {
            setOpen(false);
          }
        }, 0);
      };
      // Keep the chevron's aria-expanded in sync when the theme opens/closes via
      // hover or the trigger's own Down-arrow handler.
      var mo = new MutationObserver(function () {
        var open = item.classList.contains('is-open');
        if ((chevron.getAttribute('aria-expanded') === 'true') !== open) {
          chevron.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
      });

      chevron.addEventListener('click', onClick);
      chevron.addEventListener('keydown', onKeydown);
      item.addEventListener('focusout', onItemFocusOut);
      mo.observe(item, { attributes: true, attributeFilter: ['class'] });

      // Insert as the second tab stop: immediately after the trigger link.
      if (trigger.nextSibling) item.insertBefore(chevron, trigger.nextSibling);
      else item.appendChild(chevron);

      reverts.push(function () {
        mo.disconnect();
        chevron.removeEventListener('click', onClick);
        chevron.removeEventListener('keydown', onKeydown);
        item.removeEventListener('focusout', onItemFocusOut);
        if (chevron.parentNode) chevron.parentNode.removeChild(chevron);
      });
    });
  }

  /* ============================================================
     #3 (auditor) — Moving content with no pause mechanism. Reduce-motion
     already kills CSS animation/transitions and pauses autoplay <video>, but
     JS-driven carousels (Swiper/Flickity autoplay in banner sections) keep
     advancing. While accessibility mode is on, stop their autoplay so the
     content holds still and can be read; manual nav (arrows/swipe) still works.
     The accessibility toggle itself is the keyboard/AT-operable mechanism, and
     autoplay is restored when it's switched off. WCAG 2.2.2.
     ============================================================ */
  function enhancePauseMotion() {
    document.querySelectorAll('.swiper').forEach(function (el) {
      var sw = el.swiper;
      if (!sw) return; // not initialized yet; a later sweep will catch it
      if (processed.has(el)) return;
      processed.add(el);
      var ap = sw.autoplay;
      if (!ap) return;
      var wasRunning = (ap.running !== undefined) ? ap.running : !!(sw.params && sw.params.autoplay);
      if (!wasRunning) return;
      try { ap.stop(); } catch (e) {}
      var keepStopped = function () { try { ap.stop(); } catch (e) {} };
      if (sw.on) sw.on('autoplayStart', keepStopped); // re-stop if it restarts on interaction
      reverts.push(function () {
        if (sw.off) sw.off('autoplayStart', keepStopped);
        try { ap.start(); } catch (e) {}
      });
    });
    var Flickity = window.Flickity;
    if (Flickity && Flickity.data) {
      document.querySelectorAll('[data-autoplay="true"]').forEach(function (el) {
        if (processed.has(el)) return;
        var inst = Flickity.data(el);
        if (!inst) return;
        processed.add(el);
        if (typeof inst.stopPlayer === 'function') {
          try { inst.stopPlayer(); } catch (e) {}
          reverts.push(function () { try { inst.playPlayer(); } catch (e) {} });
        }
      });
    }
    // Issue 16: Pause native autoplay <video> elements (WCAG 2.2.2 — pause mechanism).
    document.querySelectorAll('video[autoplay]').forEach(function (video) {
      if (processed.has(video)) return;
      processed.add(video);
      try {
        video.pause();
        video.removeAttribute('autoplay');
      } catch (e) {}
      reverts.push(function () {
        try {
          video.setAttribute('autoplay', '');
          video.play().catch(function () {});
        } catch (e) {}
      });
    });
  }

  /* ============================================================
     #4 (auditor) — A dialog opened without moving focus into it. Most dialogs
     here already handle this (theme.Modals, cart drawer, search popup), so this
     is a conservative SAFETY NET: when a dialog becomes visible, wait, and only
     if focus is STILL outside it (i.e. nothing else moved it) move focus to the
     first focusable element. This never fights the handlers that already work.
     WCAG 2.4.3 / 2.1.1.
     ============================================================ */
  function dlgVisible(el) {
    if (el.getAttribute('aria-hidden') === 'true') return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }
  function enhanceDialogFocus() {
    document.querySelectorAll('[role="dialog"], .modal, dialog, .cart-drawer-content').forEach(function (dlg) {
      if (processed.has(dlg)) return;
      processed.add(dlg);
      var wasVisible = dlgVisible(dlg);
      var mo = new MutationObserver(function () {
        var vis = dlgVisible(dlg);
        if (vis && !wasVisible) {
          window.setTimeout(function () {
            if (dlgVisible(dlg) && !dlg.contains(document.activeElement)) {
              var f = dlg.querySelector('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
              if (f) { try { f.focus(); } catch (e) {} }
              else { dlg.setAttribute('tabindex', '-1'); try { dlg.focus(); } catch (e) {} }
            }
          }, 250); // after theme.Modals' ~100ms focus, so we only act if it didn't
        }
        wasVisible = vis;
      });
      mo.observe(dlg, { attributes: true, attributeFilter: ['class', 'style', 'aria-hidden', 'open'] });
      reverts.push(function () { mo.disconnect(); });
    });
  }

  /* ============================================================
     #5 & #6 (auditor) — Ambiguous link text ("Shop", "Learn more", …) and
     links sharing the same text but pointing to different destinations. For
     links with no author-set accessible name, append destination context
     (derived from the href) to the accessible name so each link's purpose and
     target are distinguishable. Visible text is unchanged. WCAG 2.4.4 / 2.4.9.
     ============================================================ */
  var AMBIGUOUS_TEXT = {
    'shop': 1, 'shop now': 1, 'shop all': 1, 'shop the collection': 1, 'learn more': 1,
    'read more': 1, 'view': 1, 'view all': 1, 'view more': 1, 'see more': 1, 'see all': 1,
    'discover': 1, 'explore': 1, 'click here': 1, 'more': 1, 'details': 1, 'buy now': 1,
    'get started': 1, 'shop collection': 1, 'view product': 1, 'add': 1
  };
  function normText(s) { return (s || '').replace(/\s+/g, ' ').trim().toLowerCase(); }
  function enhanceLinkClarity() {
    var links = Array.prototype.slice.call(document.querySelectorAll('a[href]'));
    var textHrefs = {}; // normalized text -> set of distinct hrefs (duplicate detection)
    links.forEach(function (a) {
      var t = normText(a.textContent);
      if (!t) return;
      var href = a.getAttribute('href') || '';
      (textHrefs[t] = textHrefs[t] || {})[href] = true;
    });
    links.forEach(function (a) {
      if (a.hasAttribute('aria-label') || a.hasAttribute('aria-labelledby')) return; // respect author intent
      var t = normText(a.textContent);
      if (!t) return; // empty/icon-only handled elsewhere
      var isAmbiguous = !!AMBIGUOUS_TEXT[t];
      var isDuplicate = textHrefs[t] && Object.keys(textHrefs[t]).length > 1;
      if (!isAmbiguous && !isDuplicate) return;
      var ctx = linkNameFromHref(a.getAttribute('href'));
      if (!ctx || normText(ctx) === t) return; // nothing useful / would be redundant
      var visible = (a.textContent || '').replace(/\s+/g, ' ').trim();
      setAttrTracked(a, 'aria-label', visible + ': ' + ctx);
    });
  }

  /* ============================================================
     #7 (auditor) — Links that open a new window/tab with no warning. Append
     "(opens in new window)" to the accessible name so AT announces it before
     activation. WCAG 3.2.5 / G201.
     ============================================================ */
  function hasNewWindowMention(s) { return /\b(new window|new tab)\b/i.test(s || ''); }
  function enhanceNewWindowLinks() {
    document.querySelectorAll('a[target="_blank"]').forEach(function (a) {
      var existing = a.getAttribute('aria-label');
      var base = (existing && existing.trim()) ? existing.trim() : (a.textContent || '').replace(/\s+/g, ' ').trim();
      if (!base || hasNewWindowMention(base)) return; // unnamed (handled elsewhere) or already announced
      setAttrTracked(a, 'aria-label', base + ' (opens in new window)');
    });
  }

  /* ============================================================
     Contrast overrides (WCAG 1.4.3 / 1.4.11) delivered at RUNTIME.
     These mirror the html.a11y-high-contrast rules but are injected here
     instead of relying on the inline <style> in accessibility-preferences
     .liquid — FlashSpeed's "remove unused CSS" strips rules whose selector
     (html.a11y-high-contrast …) doesn't match the STATIC HTML, because that
     class is only added at runtime. A stylesheet created by JS after load is
     not in the HTML FlashSpeed processed, so it survives. Injected only while
     accessibility mode is on; removed on toggle-off.
     ============================================================ */
  var CONTRAST_STYLE_ID = 'a11y-contrast-style';
  var CONTRAST_RULES = [
    // keep the widget usable in high-contrast
    'html.a11y-high-contrast #a11y-prefs-trigger,html.a11y-high-contrast #a11y-prefs-trigger *{background-color:#111!important;color:#fff!important}',
    // badges / discounts
    'html.a11y-high-contrast .home-discount-percentage{color:#CC0000!important}',
    'html.a11y-high-contrast .grid-product__tag--limited-time-deal{background-color:#C0392B!important}',
    'html.a11y-high-contrast .badge--secondary.grid-product__tag--limited-time-deal{color:#C0392B!important}',
    'html.a11y-high-contrast .badge--secondary.grid-product__tag--best-value,html.a11y-high-contrast .badge--secondary.grid-product__tag--sheath{color:#B85C1A!important}',
    'html.a11y-high-contrast .badge--secondary.grid-product__tag--sold-out{color:#333!important}',
    // category tab switcher
    'html.a11y-high-contrast .btn-switcher-container button:not(.active)>p{color:#595959!important}',
    // range slider
    'html.a11y-high-contrast .noUi-pips,html.a11y-high-contrast .noUi-value-sub{color:#595959!important}',
    'html.a11y-high-contrast .noUi-marker,html.a11y-high-contrast .noUi-marker-sub,html.a11y-high-contrast .noUi-marker-large{background-color:#595959!important}',
    // disabled buttons / prices / drawer titles
    'html.a11y-high-contrast .btn.disabled,html.a11y-high-contrast .btn[disabled]{color:#595959!important}',
    'html.a11y-high-contrast .product-recommendations .grid-product__price--original{color:#595959!important}',
    'html.a11y-high-contrast .drawer__scrollable .swiper .swiper-slide .collection-title{color:#595959!important}',
    // founding-story testimonials
    'html.a11y-high-contrast .feature-text,html.a11y-high-contrast .feature-author{color:#000!important}',
    // hero CTA (orange) + compliant hover
    'html.a11y-high-contrast [id^="hero-animation-"] .hero-banner--btn,html.a11y-high-contrast .sharpening-lp .hero-banner--btn{background-color:#B35200!important;color:#fff!important}',
    'html.a11y-high-contrast [id^="hero-animation-"] .hero-banner--btn:hover,html.a11y-high-contrast .sharpening-lp .hero-banner--btn:hover{background-color:#fff!important;color:#000!important}',
    // PDP badges / prices
    'html.a11y-high-contrast .pdp-bundle-save.limited-time-deal{background-color:#C0392B!important}',
    'html.a11y-high-contrast .product__price--compare{color:#595959!important}',
    'html.a11y-high-contrast .product__price-badge{color:#CC0000!important}',
    'html.a11y-high-contrast .discount-badge{color:#B34700!important}',
    // "As seen on" pill heading (light variants only)
    'html.a11y-high-contrast .as-seen-on--section:not(.bg--black):not(.color-red) h4{color:#fff!important;border-color:#fff!important}',
    // placeholders
    'html.a11y-high-contrast ::-webkit-input-placeholder{opacity:0.9!important}',
    'html.a11y-high-contrast ::-moz-placeholder{opacity:0.9!important}',
    'html.a11y-high-contrast :-ms-input-placeholder{opacity:0.9!important}',
    'html.a11y-high-contrast ::-ms-input-placeholder{opacity:0.9!important}',
    'html.a11y-high-contrast ::placeholder{opacity:0.9!important}',
    // banner-with-padding marker-pen highlight
    'html.a11y-high-contrast .banner-with-padding .overlay-heading>span,html.a11y-high-contrast .banner-with-padding .overlay-text>p,html.a11y-high-contrast .banner-with-padding .overlay-text>span{background-color:#000!important;color:#fff!important;padding:2px 8px!important;-webkit-box-decoration-break:clone!important;box-decoration-break:clone!important}',
    // --- new batch (review slider + compare-knife-series) ---
    'html.a11y-high-contrast .review-card .review,html.a11y-high-contrast .review-card .author{color:#fff!important}',
    // Review slider: text reads as light grey because non-active slides are dimmed
    // to opacity:.25 (and the active-slide opacity rule targets .swiper-slide-active,
    // not this section\'s .custom-slide-active) — restore full opacity so the (dark)
    // text meets contrast. Opacity, not color, is the real cause here.
    'html.a11y-high-contrast .reviews-2025--section .swiper-slide{opacity:1!important}',
    'html.a11y-high-contrast .compare-knife-series__slot-action--fixed{color:#374151!important}',
    'html.a11y-high-contrast .compare-knife-series__slot-label--comparing{color:#15803d!important}',
    'html.a11y-high-contrast .compare-knife-series__disclaimer{color:#595959!important}',
    // "Add to cart" button on product sliders/grids — orange #FFA100 (and its
    // orange-on-white hover) fails 4.5:1. Force solid black bg / white text with
    // padding so it passes in both rest and hover states.
    'html.a11y-high-contrast .custom-add-cart{background:#000!important;color:#fff!important;border-color:#000!important;padding:12px 30px!important}',
    'html.a11y-high-contrast .custom-add-cart:hover{background:#000!important;color:#FFA100!important;border-color:#000!important;}',
    // Banner product card series title ("Dalstrong ©") — #ababab on white (2.29:1).
    // Black-on-white for maximum contrast.
    'html.a11y-high-contrast .banner-product-card-series-title{color:#000!important}',
    // --- MOTU light-and-dark landing section (purple/grey on black) ---
    // Heading "DARK." em + 2nd product title — #703A70 on #000 (2.52:1).
    // White-on-black for maximum contrast.
    'html.a11y-high-contrast .motu-light-and-dark--heading h2 em,html.a11y-high-contrast .motu-light-and-dark--product:nth-child(2) .motu-light-and-dark--product-title{color:#fff!important}',
    // Pre-order copyright line — #666 on #0a0a0a (3.44:1). White-on-black.
    'html.a11y-high-contrast .motu-lp-info--copyright{color:#fff!important}'
  ];
  /* The non-contrast accessibility effects — also gated under runtime-added
     html.a11y-* classes, so they were being stripped from the inline <style>
     by FlashSpeed too. Delivered here for the same reason. */
  var EFFECT_RULES = [
    // 1. Easier reading — larger rem base + WCAG text spacing
    'html.a11y-easier-reading{font-size:112.5%}',
    'html.a11y-easier-reading p,html.a11y-easier-reading li,html.a11y-easier-reading dd{line-height:1.8!important;letter-spacing:0.05em!important;word-spacing:0.1em!important}',
    'html.a11y-easier-reading p{margin:.5em 0!important}',
    // 3. Reduce motion — kill CSS animation/transition (JS pauses media/carousels)
    'html.a11y-reduce-motion *,html.a11y-reduce-motion *::before,html.a11y-reduce-motion *::after{animation-duration:0.001ms!important;animation-iteration-count:1!important;transition-duration:0.001ms!important;scroll-behavior:auto!important}',
    // 4. Stronger focus outline
    'html.a11y-strong-focus a:focus-visible,html.a11y-strong-focus button:focus-visible,html.a11y-strong-focus input:focus-visible,html.a11y-strong-focus select:focus-visible,html.a11y-strong-focus textarea:focus-visible,html.a11y-strong-focus [tabindex]:focus-visible{outline:3px solid #cc5500!important;outline-offset:3px!important;box-shadow:0 0 0 5px rgba(204,85,0,0.25)!important}',
    // 5. 2025-nav "has sub-menu" chevron affordance (reveal on first-tab focus)
    'html.a11y-enabled .site-nav__details:focus-within + .nav-dropdown-toggle{position:static;width:auto;height:auto;opacity:1;pointer-events:auto;margin-left:-26px;margin-right:22px;display:inline-flex;align-items:center;justify-content:center}',
    // 6. "Accessibility Enabled" notice reveal (default #a11y-prefs-notice{display:none} stays inline)
    'html.a11y-enabled #a11y-prefs-notice{display:block;background:#f4f4f4;color:#111;padding:32px 20px;font-family:"gothambook",Arial,Helvetica,sans-serif}',
    '@media (max-width:600px){html.a11y-enabled #a11y-prefs-notice{padding:24px 16px}}',
    // Issue 10: skip link visible when focused in a11y mode (overrides visually-hidden hiding)
    'html.a11y-enabled .in-page-link.skip-link:focus,html.a11y-enabled .in-page-link.skip-link:focus-visible{position:static!important;left:auto!important;width:auto!important;height:auto!important;clip:auto!important;clip-path:none!important;overflow:visible!important;white-space:normal!important;background:#fff!important;color:#000!important;padding:12px 20px!important;z-index:999999!important;display:block!important;text-decoration:none!important;outline:3px solid #cc5500!important;outline-offset:2px!important}',
    // Issues 14 & 15: minimum 44×44px touch targets — header icon links (search, account, cart) and support/contact links
    'html.a11y-enabled .site-nav__link--icon{min-height:44px!important;min-width:44px!important;display:inline-flex!important;align-items:center!important;justify-content:center!important}',
    'html.a11y-enabled .custom-link.custom-bundles-link,html.a11y-enabled .klaviyo-chat-link{min-height:44px!important;display:inline-flex!important;align-items:center!important}'
  ];
  function injectA11yStyles() {
    if (document.getElementById(CONTRAST_STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = CONTRAST_STYLE_ID;
    style.textContent = CONTRAST_RULES.concat(EFFECT_RULES).join('\n');
    (document.head || document.documentElement).appendChild(style);
    reverts.push(function () { if (style.parentNode) style.parentNode.removeChild(style); });
  }

  /* ---- shared status-message infrastructure (WCAG 4.1.3) ---- */
  function ensureLiveRegion() {
    if (liveRegion && liveRegion.parentNode) return liveRegion;
    liveRegion = document.createElement('div');
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'a11y-enhance-live';
    liveRegion.style.cssText = 'position:absolute!important;width:1px;height:1px;margin:-1px;' +
      'padding:0;border:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;';
    (document.body || document.documentElement).appendChild(liveRegion);
    reverts.push(function () {
      if (liveRegion && liveRegion.parentNode) liveRegion.parentNode.removeChild(liveRegion);
      liveRegion = null;
    });
    return liveRegion;
  }
  function announce(msg) {
    var lr = ensureLiveRegion();
    lr.textContent = '';
    // Re-set on a tick so identical consecutive messages are still re-announced.
    window.setTimeout(function () { lr.textContent = msg; }, 50);
  }
  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      window.clearTimeout(t);
      t = window.setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  /* ============================================================
     #8 (auditor) — Interactive elements with no accessible name. Generic
     safety pass: for controls that compute to NO name at all, derive one from
     the best available signal (title, <svg><title>, data-tooltip/title/label,
     the aria-controls target's text, or a link's href). Only acts when there's
     currently NO name, so it never overrides author intent. WCAG 4.1.2.
     ============================================================ */
  function svgTitleText(el) {
    var t = el.querySelector('svg title');
    return t ? (t.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }
  function hasAccessibleName(el) {
    if ((el.textContent || '').replace(/\s+/g, ' ').trim()) return true;
    var al = el.getAttribute('aria-label');
    if (al && al.trim()) return true;
    if (el.getAttribute('aria-labelledby')) return true;
    var ti = el.getAttribute('title');
    if (ti && ti.trim()) return true;
    var img = el.querySelector('img[alt]');
    if (img && (img.getAttribute('alt') || '').trim()) return true;
    if (svgTitleText(el)) return true;
    if (el.tagName === 'INPUT' && el.value && el.value.trim()) return true;
    return false;
  }
  function deriveControlName(el) {
    var ti = el.getAttribute('title');
    if (ti && ti.trim()) return ti.trim();
    var st = svgTitleText(el);
    if (st) return st;
    var d = el.getAttribute('data-tooltip') || el.getAttribute('data-title') ||
            el.getAttribute('data-label') || el.getAttribute('data-original-title');
    if (d && d.trim()) return d.trim();
    var ctrl = el.getAttribute('aria-controls');
    if (ctrl) {
      var tgt = document.getElementById(ctrl);
      if (tgt) {
        var tt = (tgt.getAttribute('aria-label') || tgt.textContent || '').replace(/\s+/g, ' ').trim();
        if (tt) return tt.slice(0, 80);
      }
    }
    if (el.tagName === 'A') {
      var nm = linkNameFromHref(el.getAttribute('href'));
      if (nm) return nm;
    }
    return '';
  }
  function enhanceUnnamedControls() {
    eachNew('button, [role="button"], a[href], [role="link"], [role="tab"], [role="menuitem"], summary', function (el) {
      if (hasAccessibleName(el)) return;
      var name = deriveControlName(el);
      if (name) addAttr(el, 'aria-label', name);
    });
  }

  /* ============================================================
     #9 (auditor) — Adding to cart wasn't announced. Watch the cart UI (drawer
     content + count bubble) for changes and announce success and the new item
     count via the polite live region. The count comes from /cart.js so it's
     accurate regardless of which add path the theme uses. WCAG 4.1.3.
     ============================================================ */
  var cartSubscription = null;
  var cartReconnect = null;
  function announceCartSnapshot(c) {
    if (!isEnabled()) return;
    var n = c.item_count;
    if (cartLastCount === null) { cartLastCount = n; return; }
    if (n === cartLastCount) return;
    var unit = ' item' + (n === 1 ? '' : 's');
    var msg = (n > cartLastCount)
      ? 'Item added to cart. Your cart now has ' + n + unit + '.'
      : 'Item removed. Your cart now has ' + n + unit + '.';
    cartLastCount = n;
    announce(msg);
  }
  function announceCartChange() {
    if (!isEnabled()) return;
    var reads = window.theme && window.theme.cartReads;
    var request = reads ? reads.read() : window.fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
    request.then(announceCartSnapshot).catch(function () {});
  }
  function enhanceCartAnnounce() {
    var reads = window.theme && window.theme.cartReads;
    if (reads) {
      if (cartSubscription) { cartReconnect(); return; }
      // Seed from server-rendered/current header state; do not announce initial content.
      var count = document.querySelector('.cart-link__count, .cart-link__bubble-num');
      var value = count && count.textContent.trim();
      if (value && /^\d+$/.test(value)) cartLastCount = Number(value);
      cartSubscription = reads.subscribe(announceCartSnapshot);
      reverts.push(function () { cartSubscription(); cartSubscription = null; });
      // Keep detecting app DOM updates that do not emit the theme's public events.
      // Known drawer renders already trigger a fresh coordinated read.
      var knownDrawerChange = false;
      var onDrawerRefresh = function (event) {
        if (!event.detail || !event.detail.themeCartRefresh) return;
        knownDrawerChange = true;
        Promise.resolve().then(function () { knownDrawerChange = false; });
      };
      document.addEventListener('cart:drawer:refreshed', onDrawerRefresh);
      var active = true;
      var externalChange = false;
      var checkUnknownChange = debounce(function () {
        if (!active) return;
        // An app changed the cart: a read already in flight may predate it, so don't join it.
        if (externalChange) reads.invalidate();
        externalChange = false;
        announceCartChange();
      }, 600);
      var drawerRoot = null;
      var observedNodes = [];
      var observer = new MutationObserver(function (records) {
        var unknown = records.some(function (record) {
          if (!record.target.isConnected) return false;
          if (drawerRoot && drawerRoot.contains(record.target)) return !knownDrawerChange;
          var el = record.target.nodeType === 3 ? record.target.parentElement : record.target;
          if (!el) return false;
          if (el.matches('.cart-link__bubble')) {
            return el.classList.contains('cart-link__bubble--visible') !== (cartLastCount > 0);
          }
          return Number(el.textContent) !== cartLastCount;
        });
        if (unknown) { externalChange = true; checkUnknownChange(); }
      });
      cartReconnect = function () {
        var nextDrawer = document.querySelector('.cart-drawer-content');
        var nextNodes = Array.from(document.querySelectorAll('.cart-link__count, .cart-link__bubble, [data-cart-count]'));
        if (nextDrawer === drawerRoot && nextNodes.length === observedNodes.length &&
            nextNodes.every(function (node, index) { return node === observedNodes[index]; })) return;
        var replacing = Boolean(drawerRoot || observedNodes.length);
        observer.disconnect();
        drawerRoot = nextDrawer;
        observedNodes = nextNodes;
        if (drawerRoot) observer.observe(drawerRoot, { childList: true, subtree: true });
        observedNodes.forEach(function (el) {
          observer.observe(el, { childList: true, characterData: true, subtree: true, attributes: true });
        });
        if (replacing) checkUnknownChange();
      };
      cartReconnect();
      reverts.push(function () {
        active = false;
        observer.disconnect();
        cartReconnect = null;
        document.removeEventListener('cart:drawer:refreshed', onDrawerRefresh);
      });
      if (cartLastCount === null) announceCartChange();
      return;
    }
    var announceDebounced = debounce(announceCartChange, 600);
    var seen = false;
    var drawer = document.querySelector('.cart-drawer-content');
    if (drawer && !processed.has(drawer)) {
      processed.add(drawer); seen = true;
      var mo1 = new MutationObserver(function () { announceDebounced(); });
      mo1.observe(drawer, { childList: true, subtree: true });
      reverts.push(function () { mo1.disconnect(); });
    }
    document.querySelectorAll('.cart-link__count, .cart-link__bubble, [data-cart-count]').forEach(function (el) {
      if (processed.has(el)) return;
      processed.add(el); seen = true;
      var mo2 = new MutationObserver(function () { announceDebounced(); });
      mo2.observe(el, { childList: true, characterData: true, subtree: true, attributes: true });
      reverts.push(function () { mo2.disconnect(); });
    });
    if (seen && cartLastCount === null) announceCartChange(); // establish baseline
  }

  /* ============================================================
     #10 (auditor) — Predictive-search suggestions appeared without a status
     update. Watch the results container(s) and announce the number of
     suggestions via the live region. WCAG 4.1.3.
     ============================================================ */
  function countSuggestions(container) {
    var n = container.querySelectorAll('.predictive-result, [role="option"]').length;
    if (n === 0) n = container.querySelectorAll('a[href]').length;
    return n;
  }
  function announceSearchResults(container) {
    var n = countSuggestions(container);
    if (n === 0) return;              // ignore cleared/empty state to avoid noise
    if (n === searchLastCount) return;
    searchLastCount = n;
    announce(n + ' search ' + (n === 1 ? 'suggestion' : 'suggestions') + ' available.');
  }
  function enhanceSearchStatus() {
    document.querySelectorAll('#SearchResults, #predictive-search, .search-content__products').forEach(function (c) {
      if (processed.has(c)) return;
      processed.add(c);
      var deb = debounce(function () { announceSearchResults(c); }, 400);
      var mo = new MutationObserver(deb);
      mo.observe(c, { childList: true, subtree: true });
      reverts.push(function () { mo.disconnect(); });
    });
  }

  /* ============================================================
     Issue 9 — Compare-at (was/original) price has no accessible name.
     Screen readers read the strikethrough price as a bare number with no
     context. Add aria-label="Was $X" so AT users understand it is the
     original price, not the current price. WCAG 1.3.1.
     ============================================================ */
  function enhanceComparePrices() {
    eachNew('.home-card-discount', function (el) {
      var price = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (!price) return;
      setAttrTracked(el, 'aria-label', 'Was ' + price);
    });
  }

  /* ============================================================
     Issue 12 — Hero slideshow container is not identified as a
     carousel/region to assistive technology. The slide nav bullets and
     pause button are already labelled; only the wrapper lacks a role and
     name. Add role="region" + aria-roledescription="carousel" + a name.
     WCAG 1.3.1 / 4.1.2.
     ============================================================ */
  function enhanceSlideshowRegions() {
    eachNew('.slideshow-wrapper:not([role])', function (el) {
      addAttr(el, 'role', 'region');
      addAttr(el, 'aria-roledescription', 'carousel');
      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        addAttr(el, 'aria-label', 'Hero banner slideshow');
      }
    });
  }

  /* ============================================================
     Issues 6 & 18 — Informational <video> elements ship with no `controls`,
     so keyboard-only users can't pause/replay them. Reduced-motion already
     pauses autoplay (see enhancePauseMotion / WCAG 2.2.2); this adds native
     controls so the videos are keyboard-operable. Only informational videos
     are targeted — purely decorative background videos are marked
     aria-hidden="true" (see snippets/video-div.liquid) and are left alone.
     Not using eachNew() here so we never mark the node in the shared
     `processed` set that enhancePauseMotion relies on; the :not([controls])
     selector + addAttr's hasAttribute guard make it idempotent. WCAG 2.1.1.
     ============================================================ */
  function enhanceVideoControls() {
    var vids = document.querySelectorAll('video:not([controls])');
    for (var i = 0; i < vids.length; i++) {
      var v = vids[i];
      if (v.getAttribute('aria-hidden') === 'true') continue; // decorative
      addAttr(v, 'controls', '');
    }
  }

  function enhanceAll() {
    if (!isEnabled()) return;
    injectA11yStyles();
    enhanceCarousels();
    enhanceDialogs();
    enhanceRequiredFields();
    enhanceCareerFileLabel();
    enhanceFilterGroups();
    enhancePriceRange();
    enhanceTabButtons();
    enhanceIframes();
    enhanceScrollables();
    enhanceHeadingOrder();
    enhanceIconButtons();
    enhanceEmptyLinks();
    enhanceMegaMenuKeyboard();
    enhanceNavChevronAffordance();
    enhanceClassicNavChevrons();
    enhanceComparePrices();
    enhanceSlideshowRegions();
    enhanceVideoControls();
    enhancePauseMotion();
    enhanceDialogFocus();
    enhanceUnnamedControls();
    enhanceLinkClarity();   // must run before new-window so the suffix appends to the enriched name
    enhanceNewWindowLinks();
    enhanceCartAnnounce();
    enhanceSearchStatus();
  }

  /* Revert everything and reset tracking, so normal mode is untouched. */
  function teardown() {
    for (var i = reverts.length - 1; i >= 0; i--) {
      try { reverts[i](); } catch (err) {}
    }
    reverts = [];
    processed = new WeakSet();
    liveRegion = null;
    cartLastCount = null;
    searchLastCount = -1;
  }

  /* Sliders/lazy-load/section rerenders inject DOM after load, so re-sweep on
     mutations while enabled. Debounced via rAF to stay cheap. */
  var observer = null;
  var scheduled = false;
  function scheduleSweep() {
    if (scheduled) return;
    scheduled = true;
    var run = function () { scheduled = false; enhanceAll(); };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    else window.setTimeout(run, 16);
  }
  function startObserving() {
    if (observer) return;
    observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          scheduleSweep();
          return;
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  function stopObserving() {
    if (observer) { observer.disconnect(); observer = null; }
  }

  function enable() {
    enhanceAll();
    startObserving();
  }
  function disable() {
    stopObserving();
    teardown();
  }

  /* React to the user toggling accessibility mode on/off live (the toggle
     adds/removes `a11y-enabled` on <html>). */
  var wasEnabled = isEnabled();
  new MutationObserver(function () {
    var now = isEnabled();
    if (now === wasEnabled) return;
    wasEnabled = now;
    if (now) enable(); else disable();
  }).observe(ROOT, { attributes: true, attributeFilter: ['class'] });

  function boot() { if (isEnabled()) enable(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Re-sweep after full load: image-dependent measurements (e.g. whether a
  // carousel actually overflows) are only reliable once assets have settled.
  window.addEventListener('load', function () { if (isEnabled()) scheduleSweep(); });
})();
